/**
 * Cloudflare Turnstile widget.
 *
 * Renders nothing when siteKey is null, which is how staging runs without a
 * challenge so a scanner or an end-to-end suite can sign in. The parent is
 * responsible for knowing whether it needs a token.
 *
 * Two lessons carried over from Kanvo's login:
 *
 * - appearance is "interaction-only". The challenge still runs on every load
 *   and mints a token silently; the checkbox only shows when Cloudflare wants
 *   one. Never switch to size "invisible", which cannot escalate to a checkbox
 *   and turns a visitor Turnstile cannot clear into a hard lockout.
 * - Tokens are single use. After a failed attempt the parent must remount
 *   this component (change its `key`) to get a fresh widget. reset() is not
 *   enough under interaction-only: a widget that rendered hidden can stay
 *   hidden across a reset even once a checkbox is required.
 *
 * Place it below the submit button. The holder is empty until a checkbox is
 * needed, and anything that grows above the button shoves it mid-click.
 */

import { useEffect, useRef } from "react";

interface TurnstileApi {
  render: (
    element: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      "expired-callback"?: () => void;
      "error-callback"?: () => void;
      theme?: "light" | "dark" | "auto";
      appearance?: "always" | "execute" | "interaction-only";
    },
  ) => string;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (!scriptPromise) {
    scriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        scriptPromise = null;
        reject(new Error("Could not load the challenge."));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

interface TurnstileProps {
  siteKey: string | null;
  onToken: (token: string | null) => void;
}

export function Turnstile({ siteKey, onToken }: TurnstileProps) {
  const holder = useRef<HTMLDivElement>(null);
  const callback = useRef(onToken);
  callback.current = onToken;

  useEffect(() => {
    if (!siteKey || !holder.current) return;

    const element = holder.current;
    let widgetId: string | null = null;
    let active = true;

    void loadScript()
      .then(() => {
        if (!active || !window.turnstile) return;
        widgetId = window.turnstile.render(element, {
          sitekey: siteKey,
          theme: "light",
          appearance: "interaction-only",
          callback: (token) => callback.current(token),
          "expired-callback": () => callback.current(null),
          "error-callback": () => callback.current(null),
        });
      })
      .catch(() => {
        if (active) callback.current(null);
      });

    return () => {
      active = false;
      if (widgetId && window.turnstile) {
        // remove() on a widget whose host already detached can throw, and a
        // throw in cleanup breaks the remount.
        try {
          window.turnstile.remove(widgetId);
        } catch {
          // Nothing to clean up.
        }
      }
    };
  }, [siteKey]);

  if (!siteKey) return null;

  return <div ref={holder} className="flex justify-center empty:hidden" />;
}
